/**
 * API Service for Swing Trader Edge
 * Communicates with Express API server to fetch stock data from SQLite
 * Mirrors IndexedDBService interface for drop-in compatibility
 */

// In production (Railway), frontend is served by the same Express server,
// so API calls use relative paths. In development, proxy to localhost:3001.
const API_URL = process.env.REACT_APP_API_URL ?? (
  process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001'
);
const ADMIN_API_KEY = process.env.REACT_APP_ADMIN_API_KEY || '';

function adminHeaders(extra = {}) {
  return ADMIN_API_KEY
    ? { ...extra, 'x-admin-api-key': ADMIN_API_KEY }
    : extra;
}

async function readApiError(response, fallback) {
  const contentType = response.headers.get('content-type') || '';
  try {
    if (contentType.includes('application/json')) {
      const err = await response.json();
      return err.error || err.details || fallback;
    }
    const text = await response.text();
    const routeMatch = text.match(/Cannot\s+(GET|POST|PUT|DELETE)\s+([^<\s]+)/i);
    if (routeMatch) {
      return `${fallback}: server route ${routeMatch[2]} is not available. Restart the API server or deploy the latest backend.`;
    }
    return text ? `${fallback}: ${text.slice(0, 240)}` : fallback;
  } catch (_) {
    return fallback;
  }
}

/**
 * Convert snake_case DB keys to camelCase for frontend compatibility.
 * Handles special cases like '_ttm' → 'TTM'.
 */
function toCamelCase(key) {
  // Special whole-key mappings (DB → frontend)
  const SPECIAL = {
    company_name: 'companyName',
    company_description: 'companyDescription',
    return_on_equity_ttm: 'returnOnEquityTTM',
    price_sales_ratio_ttm: 'priceSalesRatioTTM',
    price_to_book_ratio_ttm: 'priceToBookRatioTTM',
    current_ratio_ttm: 'currentRatioTTM',
    dividend_yield_ttm: 'dividendYielTTM',
    price_earnings_ratio_ttm: 'priceEarningsRatioTTM',
    price_earnings_to_growth_ratio_ttm: 'priceEarningsToGrowthRatioTTM',
    gross_profit_margin_ttm: 'grossProfitMarginTTM',
    net_profit_margin_ttm: 'netProfitMarginTTM',
    debt_ratio_ttm: 'debtRatioTTM',
    piotroski_score: 'piotroskiScore',
    rating_recommendation: 'ratingRecommendation',
    gross_profit_1yr_growth: 'grossProfit_1yr_growth',
    gross_profit_3yr_growth: 'grossProfit_3yr_growth',
  };
  if (SPECIAL[key]) return SPECIAL[key];
  // Generic: leave keys that are already camelCase-ish (contain no underscores) alone
  return key;
}

function mapRecord(record) {
  if (!record) return record;
  const mapped = {};
  for (const [k, v] of Object.entries(record)) {
    mapped[toCamelCase(k)] = v;
    // Also keep original snake_case key so either works
    if (toCamelCase(k) !== k) mapped[k] = v;
  }
  return mapped;
}

function mapRecords(records) {
  return records.map(mapRecord);
}

/**
 * Get all records with filters, sorting, and pagination
 * @param {Object} filters - Filter criteria
 * @param {number} page - Page number (1-indexed)
 * @param {number} pageSize - Number of records per page
 * @param {string} sortBy - Sort field and direction (e.g., 'signal_score_desc')
 * @returns {Promise<Object>} Response with data, pagination info, and counts
 */
export const getRecords = async (filters = {}, page = 1, pageSize = 18, sortBy = 'signal_score_desc') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortBy: sortBy,
      filters: JSON.stringify(filters)
    });

    const response = await fetch(`${API_URL}/api/stocks?${params}`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch stocks');
    }

    const result = await response.json();
    result.data = mapRecords(result.data || []);
    return result;

  } catch (error) {
    console.error('Error fetching records from API:', error);
    throw error;
  }
};

/**
 * Lookup stock by symbol (case-insensitive)
 * @param {string} symbol
 * @returns {Promise<{id:number,symbol:string,companyName?:string,isEtf?:number}|null>}
 */
export const lookupStockBySymbol = async (symbol) => {
  try {
    const sym = String(symbol || '').trim();
    if (!sym) return null;

    const params = new URLSearchParams({ symbol: sym });
    const response = await fetch(`${API_URL}/api/stocks/lookup?${params}`);

    if (!response.ok) {
      if (response.status === 404) return null;
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to lookup stock');
    }

    const record = await response.json();
    return mapRecord(record);
  } catch (error) {
    console.error('Error looking up stock by symbol from API:', error);
    throw error;
  }
};

/**
 * Get record by ID
 * @param {number} id - Record ID
 * @returns {Promise<Object>} Stock record
 */
export const getRecordById = async (id) => {
  try {
    const response = await fetch(`${API_URL}/api/stocks/${id}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch stock');
    }

    const record = await response.json();
    return mapRecord(record);

  } catch (error) {
    console.error('Error fetching record by ID from API:', error);
    throw error;
  }
};

/**
 * Get summary statistics
 * @returns {Promise<Object>} Summary stats with trend cohort counts
 */
export const getSummaryStats = async () => {
  try {
    const response = await fetch(`${API_URL}/api/stats`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch stats');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching summary stats from API:', error);
    throw error;
  }
};

/**
 * Get metadata (last updated timestamp)
 * @returns {Promise<Object>} Metadata with last_updated and record_count
 */
export const getFilterOptions = async () => {
  const response = await fetch(`${API_URL}/api/filters`);
  if (!response.ok) throw new Error('Failed to fetch filter options');
  return await response.json();
};

/**
 * Fetch the complete stock universe for the client-side Screener page.
 * @returns {Promise<{data: Object[], totalCount: number}>}
 */
export const getScreenerUniverse = async () => {
  const response = await fetch(`${API_URL}/api/screener/universe`);
  if (!response.ok) {
    throw new Error(await readApiError(response, 'Failed to load the stock universe'));
  }
  const result = await response.json();
  result.data = mapRecords(result.data || []);
  return result;
};

export const getQuickScreenDefaults = async () => {
  const response = await fetch(`${API_URL}/api/quick-screens/defaults`);
  if (!response.ok) {
    throw new Error(await readApiError(response, 'Failed to fetch Quick Screen defaults'));
  }
  return await response.json();
};

export const saveQuickScreenDefaults = async (quickScreens, idToken) => {
  const response = await fetch(`${API_URL}/api/admin/quick-screens/defaults`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({ quickScreens }),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response, 'Failed to publish Quick Screen defaults'));
  }
  return await response.json();
};

export const getMetadata = async () => {
  try {
    const response = await fetch(`${API_URL}/api/metadata`);
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to fetch metadata');
    }

    return await response.json();

  } catch (error) {
    console.error('Error fetching metadata from API:', error);
    throw error;
  }
};

/**
 * Upload CSV file to API server
 * @param {File} file - CSV file to upload
 * @returns {Promise<Object>} Upload result
 */
export const uploadCSV = async (file) => {
  try {
    const uploadUrl = `${API_URL}/api/upload`;
    const formData = new FormData();
    formData.append('file', file);

    console.log('[ApiService.uploadCSV] uploading', {
      uploadUrl,
      name: file?.name,
      size: file?.size,
      type: file?.type
    });

    let response;
    try {
      response = await fetch(uploadUrl, {
        method: 'POST',
        headers: adminHeaders(),
        body: formData
      });
    } catch (fetchErr) {
      // This is the common "Failed to fetch" case
      console.error('[ApiService.uploadCSV] fetch threw before getting a response', {
        uploadUrl,
        message: fetchErr?.message,
        name: fetchErr?.name,
        stack: fetchErr?.stack
      });
      throw fetchErr;
    }

    const contentType = response.headers.get('content-type') || '';

    if (!response.ok) {
      let details = '';
      try {
        if (contentType.includes('application/json')) {
          const errorJson = await response.json();
          details = errorJson?.error || errorJson?.details || JSON.stringify(errorJson);
        } else {
          details = await response.text();
        }
      } catch (_) {
        // ignore parsing errors
      }
      throw new Error(details || `Upload failed with status ${response.status}`);
    }

    const json = await response.json();
    console.log('[ApiService.uploadCSV] upload ok', json);
    return json;

  } catch (error) {
    console.error('[ApiService.uploadCSV] Error uploading CSV to API:', error);
    throw error;
  }
};

/**
 * Check if data exists in the database
 * @returns {Promise<boolean>} True if data exists
 */
export const hasData = async () => {
  try {
    const metadata = await getMetadata();
    return metadata.record_count > 0;
  } catch (error) {
    console.error('Error checking if data exists:', error);
    return false;
  }
};

/**
 * Load CSV data from URL (for compatibility with IndexedDBService)
 * Note: This doesn't work with API backend - use uploadCSV instead
 * @deprecated Use uploadCSV for API-based loading
 */
export const loadCSVData = async (csvPath) => {
  console.warn('loadCSVData is not supported with API backend. Use uploadCSV instead.');
  throw new Error('loadCSVData is not supported with API backend. Use Admin Import to upload files.');
};

/**
 * Load from file object (for compatibility with IndexedDBService)
 * @param {File} file - File object
 * @returns {Promise<Object>} Upload result
 */
export const loadFromFileObject = async (file) => {
  return uploadCSV(file);
};

/**
 * Load Excel data (for compatibility with IndexedDBService)
 * @param {File} file - Excel file object
 * @returns {Promise<Object>} Upload result
 */
export const loadFromExcel = async (file) => {
  return uploadCSV(file);
};

export const runPipeline = async (options = { mode: 'full' }) => {
  if (typeof options === 'string') options = { mode: options };
  const response = await fetch(`${API_URL}/api/admin/run-pipeline`, {
    method: 'POST',
    headers: adminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(options)
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to start pipeline');
  }

  return await response.json();
};

export const stopPipeline = async () => {
  const response = await fetch(`${API_URL}/api/admin/stop-pipeline`, {
    method: 'POST',
    headers: adminHeaders(),
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to stop pipeline');
  }

  return await response.json();
};

export const getPipelineStatus = async () => {
  const response = await fetch(`${API_URL}/api/admin/pipeline-status`, {
    headers: adminHeaders()
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch pipeline status');
  }

  return await response.json();
};

export const getPipelineRuns = async (limit = 50) => {
  const response = await fetch(`${API_URL}/api/admin/pipeline-runs?limit=${limit}`, {
    headers: adminHeaders()
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch pipeline runs');
  }

  return await response.json();
};

export const getPipelineSchedule = async () => {
  const response = await fetch(`${API_URL}/api/admin/pipeline-schedule`, {
    headers: adminHeaders()
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, 'Failed to fetch pipeline schedule'));
  }

  return await response.json();
};

export const savePipelineSchedule = async ({ enabled, time, options }) => {
  const response = await fetch(`${API_URL}/api/admin/pipeline-schedule`, {
    method: 'POST',
    headers: adminHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({ enabled, time, options })
  });

  if (!response.ok) {
    throw new Error(await readApiError(response, 'Failed to save pipeline schedule'));
  }

  return await response.json();
};

export const exportCSV = async (filters = {}, sortBy = 'rule_of_40_desc') => {
  try {
    const response = await fetch(`${API_URL}/api/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ filters, sortBy })
    });

    if (!response.ok) {
      throw new Error(await readApiError(response, 'Failed to export CSV'));
    }

    const blob = await response.blob();
    const today = new Date().toISOString().slice(0, 10);
    const filename = `ste_export_${today}.csv`;

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename, rowCount: null };
  } catch (error) {
    console.error('Error exporting CSV:', error);
    throw error;
  }
};

export default {
  getRecords,
  getRecordById,
  lookupStockBySymbol,
  getSummaryStats,
  getFilterOptions,
  getScreenerUniverse,
  getQuickScreenDefaults,
  saveQuickScreenDefaults,
  getMetadata,
  uploadCSV,

  exportCSV,

  runPipeline,
  stopPipeline,
  getPipelineStatus,
  getPipelineRuns,
  getPipelineSchedule,
  savePipelineSchedule,

  hasData,
  loadCSVData,
  loadFromFileObject,
  loadFromExcel
};
